import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdMic, MdStop } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { loadCredentials } from '../../apiClient';
import { getAppConfig } from '../../config';
import { translate } from '../../i18n';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../_shared/readState';
import { useChatMessages } from './hooks/useChatMessages';
import { MAX_MESSAGE_LENGTH, historyForWire, rejectionForInput } from './chat-send';
import { useDictation } from './hooks/useDictation';
import { useSendChatMessage } from './hooks/useSendChatMessage';
import {
  useGetConversations,
  GET_CONVERSATIONS_QUERY_KEY,
} from './hooks/useGetConversations';
import { useDeleteConversation } from './hooks/useDeleteConversation';
import { typography } from 'theme/tokens';

function useAnimatedDots(active: boolean, interval = 500) {
  const [dots, setDots] = useState(0);
  useEffect(() => {
    if (!active) { setDots(0); return; }
    const id = setInterval(() => setDots((d) => (d + 1) % 4), interval);
    return () => clearInterval(id);
  }, [active, interval]);
  return '.'.repeat(dots || 1);
}

function groupByRelativeDate(
  items: Array<{ id: string; title: string; updatedAt: string }>,
) {
  const groups: Record<string, typeof items> = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  for (const item of items) {
    const date = new Date(item.updatedAt);
    let label: string;
    if (date >= today) label = translate('ai_assistant.today');
    else if (date >= yesterday) label = translate('ai_assistant.yesterday');
    else if (date >= weekAgo) label = translate('ai_assistant.this_week');
    else label = translate('ai_assistant.older');

    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  }
  return groups;
}

export default function AiAssistantPage() {
  const messages = useChatMessages.use.messages();
  const addMessage = useChatMessages.use.addMessage();
  const setMessages = useChatMessages.use.setMessages();
  const conversationId = useChatMessages.use.conversationId();
  const setConversationId = useChatMessages.use.setConversationId();
  const clearMessages = useChatMessages.use.clearMessages();
  const [input, setInput] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [loadingConversationId, setLoadingConversationId] = useState<
    string | null
  >(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selectedOrganization =
    useSelectedOrganization.use.selectedOrganization();
  const selectedLocation = useSelectedLocation.use.selectedLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { mutate: sendMessage, isLoading } = useSendChatMessage(
    selectedOrganization?.id,
    selectedLocation?.id,
  );

  // `useGetConversations` is `enabled: !!organizationId` and this page carried
  // NO scope guard of any kind, so with no organization selected React Query
  // v4 held `isLoading: true` / `fetchStatus: 'idle'` and the sidebar spinner
  // below never stopped.
  //
  // Classification happens on the RAW `data`, and the `?? []` is applied
  // AFTER. The old `data: conversations = []` destructure erased the only fact
  // separating "we asked and there are none" from "nobody asked" — the
  // `TimeWindowGroupList` trap from run 105.
  const {
    data: conversationsData,
    isInitialLoading: isLoadingConversations,
    isError: isConversationsError,
  } = useGetConversations(selectedOrganization?.id);
  const conversationsReadState = classifyRead(
    isConversationsError,
    isLoadingConversations,
    conversationsData,
  );
  // Memoised, not a bare `?? []`: the fallback allocates a fresh array on
  // every render, and `grouped` below is a `useMemo` keyed on it, so an
  // unmemoised fallback re-groups on every render. The old destructuring
  // default (`data: conversations = []`) had the identical instability and
  // merely hid it from the exhaustive-deps rule.
  const conversations = useMemo(() => conversationsData ?? [], [conversationsData]);
  const { mutate: deleteConversation } = useDeleteConversation(
    selectedOrganization?.id,
  );

  const pendingAutoSendRef = useRef(false);

  const handleTranscript = useCallback(
    (text: string, autoStopped: boolean) => {
      setInput((prev) => (prev ? `${prev} ${text}` : text));
      if (autoStopped) {
        pendingAutoSendRef.current = true;
      }
    },
    [],
  );
  const { state: dictationState, error: dictationError, audioLevel, toggle: toggleDictation } =
    useDictation(selectedOrganization?.id, handleTranscript);
  const animatedDots = useAnimatedDots(dictationState === 'recording');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (dictationError) {
      toast({
        title: translate('ai_assistant.dictation_error'),
        description: dictationError,
        status: 'error',
        duration: 5000,
      });
    }
  }, [dictationError, toast]);

  const handleSendRef = useRef<() => void>(() => {});

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    /*
     * DASHBOARD-3X: refuse an over-long message BEFORE it reaches the
     * transcript. `addMessage` below is unconditional and the store is what
     * the next turn replays, so appending one the API will refuse wedged the
     * whole conversation — every later send 400s on the same entry. The text
     * stays in the composer on purpose, so it can be shortened and resent.
     */
    if (rejectionForInput(input) === 'too-long') {
      toast({
        title: translate('ai_assistant.message_too_long_title'),
        description: translate('ai_assistant.message_too_long')
          .replace('{max}', String(MAX_MESSAGE_LENGTH))
          .replace('{count}', String(trimmed.length)),
        status: 'error',
        duration: 6000,
      });
      return;
    }

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    addMessage(userMessage);
    setInput('');

    sendMessage(
      { messages: historyForWire(updatedMessages), conversationId },
      {
        onSuccess: (response) => {
          addMessage({
            role: response.role,
            content: response.content,
            actions: response.actions,
          });
          setConversationId(response.conversationId);
          queryClient.invalidateQueries({
            queryKey: GET_CONVERSATIONS_QUERY_KEY(selectedOrganization?.id ?? ''),
          });
        },
        onError: (error: any) => {
          addMessage({
            role: 'assistant',
            content: `Error: ${error?.message || translate('ai_assistant.error')}`,
            // Locally generated — never replayed to the API or the model.
            isLocalError: true,
          });
        },
      },
    );
  }, [input, isLoading, messages, addMessage, sendMessage, conversationId, setConversationId, queryClient, selectedOrganization?.id, toast]);

  handleSendRef.current = handleSend;

  // Auto-send after silence-triggered transcription completes
  useEffect(() => {
    if (dictationState === 'idle' && pendingAutoSendRef.current) {
      pendingAutoSendRef.current = false;
      handleSendRef.current();
    }
  }, [dictationState]);

  const handleLoadConversation = async (id: string) => {
    if (id === conversationId) return;
    setLoadingConversationId(id);
    try {
      const { idToken } = loadCredentials();
      const response = await fetch(
        `${getAppConfig().apiUrl}/ai-assistant/conversations/${id}`,
        {
          headers: {
            authorization: idToken,
            'x-organization-id': selectedOrganization?.id,
          },
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Request failed (${response.status})`);
      }
      const data = await response.json();
      setMessages(data.messages);
      setConversationId(data.id);
    } catch (error: any) {
      toast({
        title: translate('ai_assistant.error'),
        description: error?.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoadingConversationId(null);
    }
  };

  const handleDelete = (id: string) => {
    deleteConversation(id, {
      onError: (error: any) => {
        toast({
          title: translate('ai_assistant.error'),
          description: error?.message,
          status: 'error',
          duration: 5000,
        });
      },
    });
    if (conversationId === id) {
      clearMessages();
    }
  };

  const userBg = useColorModeValue('brand.500', 'brand.400');
  const assistantBg = useColorModeValue('gray.100', 'gray.700');
  const assistantColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sidebarBg = useColorModeValue('gray.50', 'gray.800');
  const activeItemBg = useColorModeValue('brand.50', 'brand.900');
  const hoverItemBg = useColorModeValue('gray.100', 'gray.700');
  const chatBg = useColorModeValue('white', 'gray.900');

  const grouped = useMemo(() => groupByRelativeDate(conversations), [conversations]);

  return (
    <Flex h="calc(100vh - 160px)" gap="0">
      {/* Sidebar */}
      <VStack
        w="260px"
        minW="260px"
        h="100%"
        bg={sidebarBg}
        borderRight="1px solid"
        borderColor={borderColor}
        borderRadius="12px 0 0 12px"
        p="12px"
        align="stretch"
        spacing="8px"
        overflowY="auto"
      >
        <Button
          leftIcon={<AddIcon />}
          size="sm"
          colorScheme="brand"
          variant="outline"
          onClick={clearMessages}
          w="100%"
        >
          {translate('ai_assistant.new_chat')}
        </Button>

        {conversationsReadState === 'loading' && (
          <Flex justify="center" py="16px">
            <Spinner size="sm" />
          </Flex>
        )}
        {/*
          * Deliberately a compact line rather than `ScopePrompt`: this is a
          * 260px sidebar, and `ScopePrompt` is a 48px icon over a 360px-wide
          * headline with 40px of vertical padding — the same objection
          * `readState.ts` records against putting `ErrorState` in the navbar
          * dropdown. The sentence is the shared borrowed key, so it cannot
          * drift from the fourteen pages that render it full-size.
          */}
        {conversationsReadState === 'never-issued' && (
          <Text fontSize="xs" color="gray.500" px="8px">
            {translate(scopePromptI18nKey('organization') as any)}
          </Text>
        )}
        {/*
          * `unreadable`, not the raw `isError`: a background refetch that
          * fails while the sidebar already holds conversations keeps them
          * listed instead of replacing them with an error line (R96-3).
          */}
        {conversationsReadState === 'unreadable' && (
          <Text fontSize="xs" color="red.500" px="8px">
            {translate('ai_assistant.error')}
          </Text>
        )}
        {Object.entries(grouped).map(([label, items]) => (
          <Box key={label}>
            <Text
              fontSize="xs"
              fontWeight="600"
              color="gray.500"
              textTransform="uppercase"
              mb="4px"
              mt="8px"
              px="8px"
            >
              {label}
            </Text>
            {items.map((item) => (
              <Flex
                key={item.id}
                px="8px"
                py="6px"
                borderRadius="6px"
                cursor="pointer"
                bg={conversationId === item.id ? activeItemBg : 'transparent'}
                _hover={{ bg: hoverItemBg }}
                onClick={() => handleLoadConversation(item.id)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                align="center"
                justify="space-between"
              >
                <Text
                  fontSize="sm"
                  noOfLines={1}
                  flex="1"
                  fontWeight={conversationId === item.id ? '600' : '400'}
                >
                  {loadingConversationId === item.id ? (
                    <Spinner size="xs" mr="6px" />
                  ) : null}
                  {item.title || translate('ai_assistant.untitled')}
                </Text>
                {hoveredId === item.id && (
                  <IconButton
                    aria-label={translate('ai_assistant.delete_conversation')}
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    ml="4px"
                  />
                )}
              </Flex>
            ))}
          </Box>
        ))}
      </VStack>

      {/* Main chat area */}
      <Flex
        flex="1"
        direction="column"
        bg={chatBg}
        borderRadius="0 12px 12px 0"
        p="20px"
      >
        <Text {...typography.pageTitle} mb="16px">
          {translate('ai_assistant.title')}
        </Text>

        {/* Messages area */}
        <Box flex="1" overflowY="auto" mb="16px" pr="8px">
          {messages.length === 0 && (
            <Flex justify="center" align="center" h="100%" opacity={0.5}>
              <Text>{translate('ai_assistant.empty_state')}</Text>
            </Flex>
          )}
          {messages.map((msg, i) => (
            <Flex
              key={i}
              justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
              mb="12px"
            >
              <Box
                maxW="70%"
                px="16px"
                py="10px"
                borderRadius="12px"
                bg={msg.role === 'user' ? userBg : assistantBg}
                color={msg.role === 'user' ? 'white' : assistantColor}
              >
                {msg.role === 'assistant' ? (
                  <Box
                    sx={{
                      '& p': { margin: 0 },
                      '& p + p': { mt: '8px' },
                      '& ul, & ol': { pl: '20px', my: '4px' },
                      '& code': {
                        bg: 'blackAlpha.200',
                        px: '4px',
                        borderRadius: '4px',
                        fontSize: 'sm',
                      },
                      '& pre': {
                        bg: 'blackAlpha.200',
                        p: '8px',
                        borderRadius: '4px',
                        overflowX: 'auto',
                        my: '4px',
                      },
                    }}
                  >
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                    {msg.actions?.length ? (
                      <HStack mt="8px" spacing="8px">
                        {msg.actions.map((action, j) => (
                          <Button
                            key={j}
                            size="sm"
                            variant="outline"
                            colorScheme="brand"
                            onClick={() => navigate(action.path)}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </HStack>
                    ) : null}
                  </Box>
                ) : (
                  <Text whiteSpace="pre-wrap">{msg.content}</Text>
                )}
              </Box>
            </Flex>
          ))}
          {isLoading && (
            <Flex justify="flex-start" mb="12px">
              <Box px="16px" py="10px" borderRadius="12px" bg={assistantBg}>
                <Spinner size="sm" />
              </Box>
            </Flex>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input area */}
        <Flex
          borderTop="1px solid"
          borderColor={borderColor}
          pt="12px"
          gap="8px"
        >
          <Tooltip
            label={
              dictationState === 'recording'
                ? translate('ai_assistant.stop_recording')
                : dictationState === 'transcribing'
                  ? translate('ai_assistant.transcribing')
                  : translate('ai_assistant.start_recording')
            }
          >
            <Box position="relative" display="inline-flex">
              {dictationState === 'recording' && audioLevel > 0.01 && (
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  width={`${40 + audioLevel * 24}px`}
                  height={`${40 + audioLevel * 24}px`}
                  borderRadius="md"
                  bg={`rgba(229, 62, 62, ${(0.15 + audioLevel * 0.25).toFixed(2)})`}
                  transition="all 0.1s ease-out"
                  pointerEvents="none"
                />
              )}
              <IconButton
                aria-label={translate('ai_assistant.dictate')}
                icon={
                  dictationState === 'transcribing' ? (
                    <Spinner size="sm" />
                  ) : dictationState === 'recording' ? (
                    <MdStop />
                  ) : (
                    <MdMic />
                  )
                }
                colorScheme={dictationState === 'recording' ? 'red' : 'gray'}
                variant={dictationState === 'recording' ? 'solid' : 'outline'}
                onClick={toggleDictation}
                isDisabled={isLoading || dictationState === 'transcribing'}
              />
            </Box>
          </Tooltip>
          <Box flex="1" position="relative">
            <Input
              w="100%"
              placeholder={
                dictationState === 'recording'
                  ? undefined
                  : translate('ai_assistant.placeholder')
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              isDisabled={isLoading || dictationState === 'recording'}
            />
            {dictationState === 'recording' && !input && (
              <Text
                position="absolute"
                top="50%"
                left="16px"
                transform="translateY(-50%)"
                color="gray.400"
                fontSize="md"
                pointerEvents="none"
              >
                {translate('ai_assistant.listening').replace('...', '')}{animatedDots}
              </Text>
            )}
          </Box>
          <Button
            colorScheme="brand"
            onClick={handleSend}
            isLoading={isLoading}
            isDisabled={!input.trim()}
          >
            {translate('ai_assistant.send')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
